import React from 'react';
import styled, { useTheme } from 'styled-components';
import { variant } from 'styled-system';
import { buttons, buttonSizes, ButtonTone, getTone } from '../../theme/buttons';
import { solvSystem, SolvSystemProps, sx, WithSx } from '../../theme/system';
import { SolvThemeType } from '../../theme/theme';
import { typography } from '../../theme/typography';
import { style } from '../../utils/css';
import BaseClickable, {
  BaseClickableProps,
} from '../BaseClickable/BaseClickable';
import Box from '../Box/Box';
import Spinner from '../Spinner/Spinner';

export type ButtonProps = SolvSystemProps &
  WithSx &
  Omit<BaseClickableProps, 'showAccessory' | 'accessory' | 'contentPRops'> & {
    /** Which button type eto display */
    variant?: keyof typeof buttons;

    /** Which button size to display */
    size?: keyof typeof buttonSizes;

    /** Whether to render the button at full width */
    fullWidth?: boolean;

    /** What color to render the button as */
    tone?: ButtonTone;
  };

const StyledButton = styled(BaseClickable)<
  ButtonProps & {
    $size?: keyof typeof buttonSizes;
  }
>(
  {
    transition: 'background 75ms, box-shadow .15s, transform 25ms',
  },
  style(typography.button),
  variant({
    variants: buttons,
  }),
  variant({
    prop: '$size',
    variants: buttonSizes,
  }),
  sx,
  solvSystem,
);

const Button = ({
  children,
  loading,
  fullWidth,
  startIcon,
  endIcon,
  tone = 'primary',
  variant = 'contained',
  size = 'medium',
  ...props
}: ButtonProps) => {
  const { colors } = useTheme() as SolvThemeType;
  const toneColors = getTone(colors)[tone];
  return (
    <StyledButton
      variant={variant}
      $size={size}
      borderRadius="base"
      justifyContent="center"
      startIcon={startIcon}
      endIcon={endIcon}
      loading={!!loading}
      style={{
        pointerEvents: loading ? 'none' : 'auto',
        ...(fullWidth ? { width: '100%' } : {}),
        ...style({
          pl: startIcon ? 2.5 : 3,
          pr: endIcon ? 2.5 : 3,
          '--focus-offset': '2px',
          ...props.sx,
        }),
        ...toneColors,
      }}
      showAccessory={!!loading}
      accessory={
        <Box
          position="absolute"
          left="0"
          top="0"
          bottom="0"
          right="0"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner size="lg" color="crunchberry-light" />
        </Box>
      }
      {...props}
    >
      {children}
    </StyledButton>
  );
};

Button.displayName = 'Button';
export default Button;
